/**
 * CouponUsagePanel — the right column of the recoverable-carts view.
 *
 * An ADMIN OVERVIEW, not a card gallery: "how many coupons are running, and how
 * are they doing?" at a glance — a compact stat header + a dense, scrollable
 * table of every code (active first) with redemptions / revenue / carts. Built
 * to stay readable at 3 coupons or 30.
 *
 * Data: zaviago_backend.api.coupon_analytics.get_live_coupon_usage (session
 * company). Until that endpoint is deployed the panel renders representative
 * PREVIEW data (clearly badged) so the layout can be reviewed.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TicketPercent, ExternalLink, ChevronDown, AlertTriangle } from 'lucide-react'
import { frappeMethod } from '@/lib/erp/utils'

const NUM = new Intl.NumberFormat('en-US')
const money = (n: number) =>
  n >= 1_000_000 ? `฿${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `฿${(n / 1000).toFixed(n >= 100_000 ? 0 : 1)}k` : `฿${NUM.format(n)}`

export interface CouponCodeUsage {
  code: string
  redemptions: number
  revenue: number
  discount: number
  net_revenue: number
  roi: number | null
  aov: number
  carts_applied: number
  conversion: number
  limit: number
  used: number
  valid_upto: string | null
  store: string | null
  first_used: string | null
  enabled: boolean
}
export interface RecentRedemption { so: string; code: string; amount: number; when: string }
export interface CouponUsage {
  codes: CouponCodeUsage[]
  recent: RecentRedemption[]
  totals: {
    redemptions: number
    revenue: number
    discount: number
    net_revenue: number
    discount_pct: number
    roi: number | null
    conversion: number
    carts_applied: number
    codes: number
  }
}

// Representative preview until the backend endpoint is live — several coupons so
// the dense table reads true at scale.
const PREVIEW: CouponUsage = {
  codes: [
    { code: 'SUBDAY', redemptions: 24, revenue: 39600, discount: 7128, net_revenue: 32472, roi: 5.6, aov: 1650, carts_applied: 31, conversion: 43.6, limit: 200, used: 24, valid_upto: '2026-09-26', store: 'Sub Scrub', first_used: '2026-09-24', enabled: true },
    { code: 'PAYDAY09', redemptions: 7, revenue: 13230, discount: 2381, net_revenue: 10849, roi: 5.6, aov: 1890, carts_applied: 22, conversion: 24.1, limit: 200, used: 7, valid_upto: '2026-09-26', store: 'Cside', first_used: '2026-09-24', enabled: true },
    { code: 'FREESHIP150', redemptions: 41, revenue: 61500, discount: 2050, net_revenue: 59450, roi: 30, aov: 1500, carts_applied: 12, conversion: 77.4, limit: 0, used: 41, valid_upto: null, store: 'Cside', first_used: '2026-09-10', enabled: true },
    { code: 'NEWYEAR', redemptions: 5, revenue: 9450, discount: 1890, net_revenue: 7560, roi: 5, aov: 1890, carts_applied: 3, conversion: 62.5, limit: 50, used: 47, valid_upto: '2026-09-30', store: 'Sub Scrub', first_used: '2026-09-18', enabled: true },
    { code: 'VIP20', redemptions: 0, revenue: 0, discount: 0, net_revenue: 0, roi: null, aov: 0, carts_applied: 4, conversion: 0, limit: 100, used: 0, valid_upto: '2026-10-15', store: null, first_used: null, enabled: true },
    { code: 'WELCOME10', redemptions: 18, revenue: 27000, discount: 2700, net_revenue: 24300, roi: 10, aov: 1500, carts_applied: 0, conversion: 100, limit: 0, used: 18, valid_upto: null, store: 'Cside', first_used: '2026-08-02', enabled: false },
    { code: 'SONGKRAN', redemptions: 63, revenue: 94500, discount: 14175, net_revenue: 80325, roi: 6.7, aov: 1500, carts_applied: 0, conversion: 100, limit: 0, used: 63, valid_upto: null, store: 'Sub Scrub', first_used: '2026-04-11', enabled: false },
  ],
  recent: [
    { so: 'SO-SHOP-04833', code: 'SUBDAY', amount: 1650, when: '2026-09-25T10:00:00' },
    { so: 'SO-SHOP-04829', code: 'PAYDAY09', amount: 1890, when: '2026-09-25T09:12:00' },
    { so: 'SO-SHOP-04821', code: 'FREESHIP150', amount: 1500, when: '2026-09-25T08:31:00' },
    { so: 'SO-SHOP-04818', code: 'SUBDAY', amount: 1650, when: '2026-09-25T07:48:00' },
  ],
  totals: { redemptions: 158, revenue: 245280, discount: 30324, net_revenue: 214956, discount_pct: 12.4, roi: 8.1, conversion: 68.7, carts_applied: 72, codes: 6 },
}

function useCouponUsage() {
  const q = useQuery<CouponUsage>({
    queryKey: ['coupon-usage-live'],
    queryFn: () => frappeMethod<CouponUsage>('zaviago_backend.api.coupon_analytics.get_live_coupon_usage'),
    staleTime: 60 * 1000,
    retry: false,
  })
  const isPreview = q.isError || (!q.isLoading && !q.data)
  return { data: isPreview ? PREVIEW : q.data, isPreview, isLoading: q.isLoading }
}

/** Compact stat cell for the overview strip. */
function Stat({ value, label, tone }: { value: string; label: string; tone?: 'accent' | 'muted' }) {
  return (
    <div className="min-w-0">
      <p className={`dash-metric-sm ${tone === 'muted' ? 'text-muted-foreground' : 'text-foreground'}`}>
        {value}
      </p>
      <p className="mt-1 dash-meta text-muted-foreground">{label}</p>
    </div>
  )
}

function timeAgoTh(iso: string): string {
  const then = new Date(iso.replace(' ', 'T')).getTime()
  if (Number.isNaN(then)) return ''
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 1) return 'เมื่อกี้'
  if (mins < 60) return `${mins} น.`
  const hrs = Math.round(mins / 60)
  return hrs < 24 ? `${hrs} ชม.` : `${Math.round(hrs / 24)} วัน`
}

type SortKey = 'redemptions' | 'revenue' | 'discount'

export function CouponUsagePanel() {
  const { data, isPreview, isLoading } = useCouponUsage()
  const [sort, setSort] = useState<SortKey>('redemptions')
  const [showRecent, setShowRecent] = useState(false)

  const codes = data?.codes ?? []
  const running = codes.filter((c) => c.enabled).length
  const totals = data?.totals

  const rows = useMemo(() => {
    // Active first, then by the chosen metric.
    return [...codes].sort(
      (a, b) => (a.enabled === b.enabled ? b[sort] - a[sort] : a.enabled ? -1 : 1),
    )
  }, [codes, sort])

  const HeadBtn = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => setSort(k)}
      className={`tabular-nums transition-colors hover:text-foreground ${sort === k ? 'text-foreground' : ''}`}
    >
      {children}
    </button>
  )

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <TicketPercent className="h-4 w-4 text-muted-foreground" />
        <h3 className="dash-title text-foreground">คูปอง</h3>
        <span className="rounded-full bg-emerald-500/12 px-1.5 py-px dash-meta font-medium text-emerald-600 dark:text-emerald-400">
          {running} กำลังใช้
        </span>
        {isPreview && (
          <span
            className="ml-auto rounded-full bg-amber-500/12 px-1.5 py-px dash-micro font-medium text-amber-600 dark:text-amber-400"
            title="ยังไม่ได้ deploy endpoint — แสดงข้อมูลตัวอย่างเพื่อดูเลย์เอาต์"
          >
            ตัวอย่าง
          </span>
        )}
      </div>

      {/* Overview strip — the "how are we doing overall" answer */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-muted/40 px-3.5 py-3">
        <Stat value={NUM.format(totals?.redemptions ?? 0)} label="ครั้งที่ใช้" />
        <Stat value={money(totals?.net_revenue ?? 0)} label="รายได้สุทธิ (หักส่วนลด)" />
        <Stat value={money(totals?.discount ?? 0)} label={`ส่วนลดที่ให้ไป · ${totals?.discount_pct ?? 0}%`} tone="muted" />
        <Stat value={`${totals?.conversion ?? 0}%`} label="แปลงเป็นออเดอร์" tone="muted" />
      </div>
      {/* Efficiency + funnel one-liner */}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 dash-meta text-muted-foreground">
        <span>รายได้รวม <span className="tabular-nums text-foreground/80">{money(totals?.revenue ?? 0)}</span></span>
        {totals?.roi != null && (
          <>
            <span className="opacity-40">·</span>
            <span>ROI <span className="tabular-nums font-medium text-emerald-600 dark:text-emerald-400">฿{totals.roi}</span> ต่อส่วนลด ฿1</span>
          </>
        )}
        <span className="opacity-40">·</span>
        <span><span className="tabular-nums text-foreground/80">{NUM.format(totals?.carts_applied ?? 0)}</span> ตะกร้ากำลังใส่โค้ด</span>
      </div>

      {/* Dense table — scales to many coupons */}
      {isLoading ? (
        <div className="mt-3 space-y-1.5">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-6 animate-pulse rounded bg-muted/50" />)}
        </div>
      ) : codes.length === 0 ? (
        <p className="mt-3 dash-meta text-muted-foreground">ยังไม่มีการใช้คูปองในช่วงนี้</p>
      ) : (
        <div className="mt-3">
          {/* Column header */}
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 border-b border-border/60 pb-1.5 dash-meta font-medium uppercase tracking-wide text-muted-foreground">
            <span>โค้ด</span>
            <span className="w-10 text-right"><HeadBtn k="redemptions">ใช้</HeadBtn></span>
            <span className="w-16 text-right"><HeadBtn k="revenue">รายได้</HeadBtn></span>
            <span className="w-16 text-right"><HeadBtn k="discount">ส่วนลด</HeadBtn></span>
          </div>
          {/* Rows */}
          <div className="max-h-64 overflow-y-auto scrollbar-none divide-y divide-border/40">
            {rows.map((c) => {
              const nearLimit = c.limit > 0 && c.used / c.limit >= 0.8
              const daysLeft = c.valid_upto
                ? Math.ceil((new Date(`${c.valid_upto}T23:59:59`).getTime() - Date.now()) / 86_400_000)
                : null
              const expiring = daysLeft != null && daysLeft >= 0 && daysLeft <= 3
              const warn = c.enabled && (nearLimit || expiring)
              const warnText = [
                nearLimit ? `ใกล้เต็มโควตา ${NUM.format(c.used)}/${NUM.format(c.limit)}` : null,
                expiring ? `หมดอายุใน ${daysLeft} วัน` : null,
              ].filter(Boolean).join(' · ')
              const tip = [
                c.aov ? `AOV ฿${NUM.format(c.aov)}` : null,
                c.redemptions + c.carts_applied > 0 ? `แปลงออเดอร์ ${c.conversion}%` : null,
                c.limit > 0 ? `ใช้ ${NUM.format(c.used)}/${NUM.format(c.limit)}` : null,
                c.valid_upto ? `หมดอายุ ${c.valid_upto}` : null,
                c.first_used ? `ตั้งแต่ ${c.first_used}` : null,
              ].filter(Boolean).join(' · ') || undefined
              return (
                <div
                  key={c.code}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 py-1.5 dash-meta"
                  title={tip}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                      title={c.enabled ? 'กำลังใช้' : 'ปิดอยู่'}
                    />
                    <span className="truncate font-mono dash-meta font-medium text-foreground">{c.code}</span>
                    {c.store && (
                      <span className="shrink-0 rounded bg-muted px-1 dash-micro text-muted-foreground">{c.store}</span>
                    )}
                    {warn && (
                      <span title={warnText} className="shrink-0">
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                      </span>
                    )}
                  </span>
                  <span className="w-10 text-right text-sm font-semibold tabular-nums text-foreground">{NUM.format(c.redemptions)}</span>
                  <span className="w-16 text-right tabular-nums text-muted-foreground">{money(c.revenue)}</span>
                  <span className="w-16 text-right tabular-nums text-rose-600/90 dark:text-rose-400/90">
                    {c.discount ? `-${money(c.discount)}` : '—'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Recent redemptions — collapsed by default to save space */}
      {(data?.recent?.length ?? 0) > 0 && (
        <div className="mt-2.5 border-t border-border/40 pt-2">
          <button
            type="button"
            onClick={() => setShowRecent((s) => !s)}
            className="flex w-full items-center gap-1 dash-micro font-medium text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${showRecent ? 'rotate-180' : ''}`} />
            ล่าสุดที่ใช้โค้ด
            {!showRecent && (
              <span className="ml-auto truncate font-normal text-muted-foreground/70">
                {data!.recent[0].code} · {money(data!.recent[0].amount)} · {timeAgoTh(data!.recent[0].when)}
              </span>
            )}
          </button>
          {showRecent && (
            <div className="mt-1.5 space-y-1">
              {data!.recent.slice(0, 6).map((r) => (
                <div key={r.so} className="flex items-center gap-2 dash-meta">
                  <span className="shrink-0 rounded bg-primary/10 px-1 font-mono dash-meta font-medium text-primary">{r.code}</span>
                  <span className="min-w-0 truncate text-muted-foreground">{r.so}</span>
                  <span className="ml-auto shrink-0 font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">฿{NUM.format(r.amount)}</span>
                  <span className="shrink-0 dash-meta tabular-nums text-muted-foreground/70">{timeAgoTh(r.when)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Link
        to="/analytics/coupons"
        className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1.5 dash-meta font-medium text-primary hover:bg-primary/15"
      >
        ดูรายละเอียดคูปอง
        <ExternalLink className="h-3 w-3" />
      </Link>
    </section>
  )
}
